import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/StatsDashboard.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatsDashboard = ({ isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('stats'); // 'stats', 'opponents', 'achievements'
  const [opponentStats, setOpponentStats] = useState([]);
  const [achievements, setAchievements] = useState({ unlocked: [], locked: [], stats: {} });
  const [loading, setLoading] = useState(false);

  const loadOpponentStats = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/profile/opponent-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOpponentStats(data.opponent_stats || []);
      }
    } catch (error) {
      logger.error('Error loading opponent stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadAchievements = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/profile/achievements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAchievements({
          unlocked: data.unlocked || [],
          locked: data.locked || [],
          stats: data.stats || {}
        });
      }
    } catch (error) {
      logger.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      loadAchievements();
      loadOpponentStats();
    }
  }, [isOpen, loadAchievements, loadOpponentStats]);

  if (!isOpen) return null;

  return (
    <div className="stats-dashboard-overlay" onClick={onClose}>
      <div className="stats-dashboard" onClick={(e) => e.stopPropagation()}>
        <div className="dashboard-header">
          <h2>📊 Statistics Dashboard</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-tabs">
          <button
            className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📈 Overview
          </button>
          <button
            className={`tab-btn ${activeTab === 'opponents' ? 'active' : ''}`}
            onClick={() => setActiveTab('opponents')}
          >
            ⚔️ Opponents
          </button>
          <button
            className={`tab-btn ${activeTab === 'achievements' ? 'active' : ''}`}
            onClick={() => setActiveTab('achievements')}
          >
            🏆 Achievements
          </button>
        </div>

        <div className="dashboard-content">
          {loading && <div className="loading-spinner">Loading...</div>}

          {/* Overview Tab */}
          {activeTab === 'stats' && !loading && (
            <div className="overview-section">
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">🏆</div>
                  <div className="stat-value">{achievements.stats.total_wins || 0}</div>
                  <div className="stat-label">Total Wins</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🎮</div>
                  <div className="stat-value">{achievements.stats.total_games || 0}</div>
                  <div className="stat-label">Games Played</div>
                </div>
                <div className="stat-card streak">
                  <div className="stat-icon">🔥</div>
                  <div className="stat-value">{achievements.stats.current_streak || 0}</div>
                  <div className="stat-label">Current Streak</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">⭐</div>
                  <div className="stat-value">{achievements.stats.best_streak || 0}</div>
                  <div className="stat-label">Best Streak</div>
                </div>
              </div>

              <div className="achievement-progress">
                <h3>Achievement Progress</h3>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${((achievements.unlocked?.length || 0) / (achievements.unlocked?.length + achievements.locked?.length || 1)) * 100}%` }}
                  />
                </div>
                <p>{achievements.unlocked?.length || 0} / {(achievements.unlocked?.length || 0) + (achievements.locked?.length || 0)} unlocked</p>
              </div>
            </div>
          )}

          {/* Opponents Tab */}
          {activeTab === 'opponents' && !loading && (
            <div className="opponents-section">
              <h3>Head-to-Head Records</h3>
              {opponentStats.length === 0 ? (
                <p className="no-data">No opponent data yet. Play some games!</p>
              ) : (
                <div className="opponents-list">
                  {opponentStats.map((stat, index) => (
                    <div key={stat.opponent_id} className="opponent-card">
                      <div className="opponent-rank">#{index + 1}</div>
                      <div className="opponent-avatar">{stat.opponent_avatar || '👤'}</div>
                      <div className="opponent-info">
                        <div className="opponent-name">{stat.opponent_name}</div>
                        <div className="opponent-record">
                          <span className="wins">{stat.wins}W</span>
                          <span className="separator">-</span>
                          <span className="losses">{stat.losses}L</span>
                          <span className="games">({stat.games_played} games)</span>
                        </div>
                      </div>
                      <div className="win-rate">
                        {stat.games_played > 0 
                          ? Math.round((stat.wins / stat.games_played) * 100) 
                          : 0}%
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && !loading && (
            <div className="achievements-section">
              {/* Unlocked Achievements */}
              <div className="achievements-group">
                <h3>🏅 Unlocked ({achievements.unlocked?.length || 0})</h3>
                <div className="achievements-grid">
                  {achievements.unlocked?.map(achievement => (
                    <div key={achievement.id} className="achievement-card unlocked">
                      <div className="achievement-icon">{achievement.icon}</div>
                      <div className="achievement-details">
                        <div className="achievement-name">{achievement.name}</div>
                        <div className="achievement-desc">{achievement.description}</div>
                      </div>
                      <div className="achievement-check">✓</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Locked Achievements */}
              <div className="achievements-group">
                <h3>🔒 Locked ({achievements.locked?.length || 0})</h3>
                <div className="achievements-grid">
                  {achievements.locked?.map(achievement => (
                    <div key={achievement.id} className="achievement-card locked">
                      <div className="achievement-icon">{achievement.icon}</div>
                      <div className="achievement-details">
                        <div className="achievement-name">{achievement.name}</div>
                        <div className="achievement-desc">{achievement.description}</div>
                      </div>
                      <div className="achievement-progress-mini">
                        {achievement.type === 'wins' && `${achievements.stats.total_wins || 0}/${achievement.requirement}`}
                        {achievement.type === 'games' && `${achievements.stats.total_games || 0}/${achievement.requirement}`}
                        {achievement.type === 'streak' && `${achievements.stats.best_streak || 0}/${achievement.requirement}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsDashboard;
